import React from "react";
import classNames from "classnames";
import { VStack } from "@design-system/VStack/VStack";
import { tagColors } from "./TagPlayground.lib";
import { LgHoverRow, LgHoverXRow, LgNormalRow } from "./TagPlaygroundMatrixLg";
import { MdHoverRow, MdHoverXRow, MdNormalRow } from "./TagPlaygroundMatrixMd";
import {
    SmHoverRow,
    SmNormalRow,
    XsHoverRow,
    XsNormalRow,
} from "./TagPlaygroundMatrixRows";
import {
    removableHeaders,
    typeHeaders2,
    typeHeaders6,
    typeHeaders8,
} from "./TagPlaygroundMatrixShared";
import styles from "./TagPlayground.module.scss";

const gridClassNames = {
    2: styles.grid2,
    6: styles.grid6,
    8: styles.grid8,
} satisfies Record<2 | 6 | 8, string>;

const MatrixGrid = ({
    columnCount,
    headers,
    children,
}: {
    columnCount: 2 | 6 | 8;
    headers: readonly string[];
    children: React.ReactNode;
}) => (
    <div className={classNames(styles.grid, gridClassNames[columnCount])}>
        <span className={styles.columnHeader}>color</span>
        {headers.map((header) => (
            <span key={header} className={styles.columnHeader}>
                {header}
            </span>
        ))}
        {children}
    </div>
);

const StateSection = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <VStack gap={8}>
        <h3 className={styles.stateTitle}>{title}</h3>
        {children}
    </VStack>
);

const SizeSection = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <VStack gap={16}>
        <h2 className={styles.sizeTitle}>{title}</h2>
        {children}
    </VStack>
);

/**
 * Literal Figma matrix: every legal size × color × outline × type cell, with
 * forced hover on interactive cells and hoverX on removable cells only.
 */
export const TagMatrix = () => (
    <section className={styles.matrix}>
        <VStack gap={4}>
            <h2 className={styles.sectionTitle}>Figma matrix</h2>
            <span className={styles.sectionDescription}>
                Illegal combinations are omitted. Hover rows are interactive;
                hover X rows are removable only.
            </span>
        </VStack>
        <SizeSection title="lg · primary">
            <StateSection title="Normal">
                <MatrixGrid columnCount={8} headers={typeHeaders8}>
                    <LgNormalRow />
                </MatrixGrid>
            </StateSection>
            <StateSection title="Hover">
                <MatrixGrid columnCount={8} headers={typeHeaders8}>
                    <LgHoverRow />
                </MatrixGrid>
            </StateSection>
            <StateSection title="Hover X">
                <MatrixGrid columnCount={2} headers={removableHeaders}>
                    <LgHoverXRow />
                </MatrixGrid>
            </StateSection>
        </SizeSection>
        <SizeSection title="md">
            <StateSection title="Normal">
                <MatrixGrid columnCount={8} headers={typeHeaders8}>
                    {tagColors.map((color) => (
                        <MdNormalRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
            <StateSection title="Hover">
                <MatrixGrid columnCount={8} headers={typeHeaders8}>
                    {tagColors.map((color) => (
                        <MdHoverRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
            <StateSection title="Hover X">
                <MatrixGrid columnCount={2} headers={removableHeaders}>
                    {tagColors.map((color) => (
                        <MdHoverXRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
        </SizeSection>
        <SizeSection title="sm">
            <StateSection title="Normal">
                <MatrixGrid columnCount={6} headers={typeHeaders6}>
                    {tagColors.map((color) => (
                        <SmNormalRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
            <StateSection title="Hover">
                <MatrixGrid columnCount={6} headers={typeHeaders6}>
                    {tagColors.map((color) => (
                        <SmHoverRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
        </SizeSection>
        <SizeSection title="xs · numeric">
            <StateSection title="Normal">
                <MatrixGrid columnCount={2} headers={typeHeaders2}>
                    {tagColors.map((color) => (
                        <XsNormalRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
            <StateSection title="Hover">
                <MatrixGrid columnCount={2} headers={typeHeaders2}>
                    {tagColors.map((color) => (
                        <XsHoverRow key={color} color={color} />
                    ))}
                </MatrixGrid>
            </StateSection>
        </SizeSection>
    </section>
);
